export interface PartnerOption {
    value: string;
    label: string;
}

export interface CatalogPartner {
    partnerId: string;
    partnerName?: string;
}

const PARTNER_ID_ALIASES: Record<string, string> = {
    ruralking: 'rural-king',
    'rural-king-b-and-m': 'rural-king-bm',
    'rural-king-b&m': 'rural-king-bm',
    ruralkingbm: 'rural-king-bm',
    truevalue: 'true-value',
    doitbestdc: 'do-it-best-dc',
    acehardware: 'ace-hardware',
    dollargeneral: 'dollar-general',
};

export function normalizePartnerId(value: string): string {
    const normalized = value.trim().toLowerCase();
    return PARTNER_ID_ALIASES[normalized] ?? normalized;
}

export function parseAccessList(value?: string | string[] | null): string[] | null {
    if (!value) return null;

    if (Array.isArray(value)) {
        const normalized = value.map(item => normalizePartnerId(item)).filter(Boolean);
        return normalized.length > 0 ? normalized : null;
    }

    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            const normalized = parsed.map(item => normalizePartnerId(String(item))).filter(Boolean);
            return normalized.length > 0 ? normalized : null;
        }
    } catch {
        // Legacy access fields are comma-separated strings.
    }

    const normalized = trimmed.split(',').map(item => normalizePartnerId(item)).filter(Boolean);
    return normalized.length > 0 ? normalized : null;
}

export function resolveAccessList(...values: Array<string | string[] | null | undefined>): string[] | null {
    for (const value of values) {
        const parsed = parseAccessList(value);
        if (parsed) return parsed;
    }
    return null;
}

export function formatPartnerLabel(partnerId: string): string {
    return partnerId
        .split('-')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
}

export function buildPartnerOptions({
    catalogPartners,
    accessValues,
    fallbackPartners,
}: {
    catalogPartners: CatalogPartner[];
    accessValues: Array<string | string[] | null | undefined>;
    fallbackPartners: PartnerOption[];
}): PartnerOption[] {
    const catalogById = new Map<string, PartnerOption>();

    for (const partner of catalogPartners) {
        const value = normalizePartnerId(partner.partnerId);
        if (!value) continue;
        catalogById.set(value, {
            value,
            label: partner.partnerName?.trim() || formatPartnerLabel(value),
        });
    }

    for (const partner of fallbackPartners) {
        const value = normalizePartnerId(partner.value);
        if (!value || catalogById.has(value)) continue;
        catalogById.set(value, { value, label: partner.label });
    }

    const allowed = resolveAccessList(...accessValues);
    const sourceIds = allowed ?? Array.from(catalogById.keys());

    return sourceIds
        .map(value => {
            const normalized = normalizePartnerId(value);
            return catalogById.get(normalized) ?? { value: normalized, label: formatPartnerLabel(normalized) };
        })
        .filter((option, index, options) => options.findIndex(candidate => candidate.value === option.value) === index)
        .sort((a, b) => a.label.localeCompare(b.label));
}
